<template>
  <div
    v-if="items.length"
    class="responsive-media-gallery"
    :style="galleryStyle"
  >
    <div v-if="usesCarousel" class="gallery-carousel-shell">
      <v-carousel
        v-model="activeIndex"
        class="gallery-carousel"
        hide-delimiter-background
        show-arrows
        :aria-label="ariaLabel"
      >
        <v-carousel-item
          v-for="(page, pageIndex) in carouselPages"
          :key="`gallery-page-${pageIndex}`"
        >
          <div class="carousel-page">
            <figure
              v-for="(item, itemIndex) in page"
              :key="itemKey(item, (pageIndex * carouselPageSize) + itemIndex)"
              class="gallery-figure carousel-figure"
            >
              <v-img
                v-if="!isVideo(item)"
                :src="item.src | mediaSrc"
                :alt="item.alt || item.title || ''"
                :position="item.position || 'center center'"
                contain
                class="gallery-image carousel-media"
              ></v-img>
              <video
                v-else
                :key="item.src | mediaSrc"
                class="gallery-video carousel-media"
                controls
                playsinline
                preload="metadata"
                :poster="item.poster"
              >
                <source :src="item.src | mediaSrc" :type="item.mimeType || 'video/mp4'" />
                <track
                  v-if="item.captionsSrc"
                  kind="captions"
                  :src="item.captionsSrc | mediaSrc"
                  srclang="en"
                  label="English"
                  default
                />
                Your browser does not support embedded video.
              </video>
              <figcaption v-if="item.title || item.caption">
                <strong v-if="item.title">{{ item.title }}</strong>
                <span v-if="item.caption">{{ item.caption }}</span>
              </figcaption>
            </figure>
          </div>
        </v-carousel-item>
      </v-carousel>
      <div class="gallery-position" aria-live="polite">
        {{ visibleRangeLabel }}
      </div>
    </div>

    <div v-else class="gallery-grid">
      <figure
        v-for="(item, index) in items"
        :key="itemKey(item, index)"
        :class="[
          'gallery-figure',
          {
            'gallery-figure-wide': item.wide,
            'gallery-figure-compact': item.compact,
          },
        ]"
      >
        <img
          v-if="!isVideo(item) && naturalImages"
          :src="item.src | mediaSrc"
          :alt="item.alt || item.title || ''"
          class="gallery-native-image"
          loading="lazy"
        />
        <v-img
          v-else-if="!isVideo(item)"
          :src="item.src | mediaSrc"
          :alt="item.alt || item.title || ''"
          :contain="item.contain === true"
          :aspect-ratio="aspectRatio"
          :position="item.position || 'center center'"
          class="gallery-image"
        ></v-img>
        <video
          v-else
          :key="item.src | mediaSrc"
          class="gallery-video"
          controls
          playsinline
          preload="metadata"
          :poster="item.poster"
        >
          <source :src="item.src | mediaSrc" :type="item.mimeType || 'video/mp4'" />
          <track
            v-if="item.captionsSrc"
            kind="captions"
            :src="item.captionsSrc | mediaSrc"
            srclang="en"
            label="English"
            default
          />
          Your browser does not support embedded video.
        </video>
        <figcaption v-if="item.title || item.caption">
          <strong v-if="item.title">{{ item.title }}</strong>
          <span v-if="item.caption">{{ item.caption }}</span>
        </figcaption>
      </figure>
    </div>
  </div>
</template>

<script>
const CAROUSEL_THRESHOLD = 6;

export default {
  name: 'ResponsiveMediaGallery',
  props: {
    items: { type: Array, default: () => [] },
    aspectRatio: { type: [Number, String], default: 1.5 },
    gridMinWidth: { type: Number, default: 260 },
    naturalImages: { type: Boolean, default: false },
    ariaLabel: { type: String, default: 'Media gallery' },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    usesCarousel() {
      return this.items.length > CAROUSEL_THRESHOLD;
    },
    carouselPageSize() {
      if (this.$vuetify.breakpoint.xs) return 1;
      if (this.$vuetify.breakpoint.sm) return 2;
      return 3;
    },
    carouselPages() {
      const pages = [];
      for (let index = 0; index < this.items.length; index += this.carouselPageSize) {
        pages.push(this.items.slice(index, index + this.carouselPageSize));
      }
      return pages;
    },
    visibleRangeLabel() {
      const first = (this.activeIndex * this.carouselPageSize) + 1;
      const last = Math.min(first + this.carouselPageSize - 1, this.items.length);
      return `${first}–${last} of ${this.items.length}`;
    },
    galleryStyle() {
      return { '--gallery-min-width': `${this.gridMinWidth}px` };
    },
  },
  methods: {
    isVideo(item) {
      return item?.type === 'video';
    },
    itemKey(item, index) {
      return item.id || item.src || `${item.title || 'media'}-${index}`;
    },
  },
};
</script>

<style scoped>
.gallery-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--gallery-min-width)), 1fr));
}

.gallery-figure {
  background: #111;
  border: 1px solid #2c2c2c;
  border-radius: 10px;
  margin: 0;
  overflow: hidden;
}

.gallery-image,
.gallery-native-image,
.gallery-video {
  background: #000;
  display: block;
  width: 100%;
}

.gallery-native-image {
  height: auto;
}

.gallery-figure-wide {
  grid-column: 1 / -1;
}

.gallery-figure-compact {
  justify-self: center;
  max-width: 560px;
  width: 100%;
}

.gallery-video {
  aspect-ratio: 1.5;
  object-fit: contain;
}

figcaption {
  color: #aaa;
  display: flex;
  flex-direction: column;
  gap: 5px;
  line-height: 1.5;
  padding: 14px 16px;
}

figcaption strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.05rem;
}

.gallery-carousel-shell {
  position: relative;
}

.gallery-carousel {
  background: #090909;
  border: 1px solid #2c2c2c;
  border-radius: 12px;
  height: clamp(430px, 62vw, 680px) !important;
  overflow: hidden;
}

.carousel-figure {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.carousel-page {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  height: 100%;
  padding: 16px;
}

.carousel-media {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  object-fit: contain;
}

.gallery-position {
  background: rgba(0, 0, 0, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  bottom: 14px;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
  left: 16px;
  padding: 6px 11px;
  pointer-events: none;
  position: absolute;
  z-index: 3;
}

@media (max-width: 600px) {
  .gallery-carousel {
    height: 460px !important;
  }

  .carousel-page {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .carousel-page {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .gallery-carousel {
    scroll-behavior: auto;
  }
}
</style>
